import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Drawer,
  Button,
  Tabs,
  Checkbox,
  Card,
  notification,
} from "antd";
import styles from "./MemberProfile.module.scss";
import textStyles from "../../Shared/Styles/TextStyles.module.scss";
import tabStyles from "../TripDetails/TripDetailsDrawer.module.scss";
import buttonStyles from "../../Shared/Styles/ButtonStyles.module.scss";
import drawerStyles from "../TripDetails/TripDetailsDrawer.module.scss";
import { IServiceUserInfo } from "../../../types/ServiceUserInfo";
import editIcon from "../../../assets/images/paymentEditIcon.svg";
import plusIcon from "../../../assets/icons/plusOutlined.svg";
import moment from "moment";
import {
  formatPhoneNumber,
  getInitialDateOfBirth,
  getRelationshipNameByRelationshipId,
} from "../../../helpers/personHelpers";
import { Address } from "../../../types/LocationTypes";
import EditProfileDrawer from "./EditProfileDrawer";
import { useServiceUserCreationCtx } from "../../../context/ServiceUserCreationContext";
import {
  TC_InitialValues,
  TrustedContact,
  defaultTC_InitialValues,
} from "../../../types/ProfileTypes";
import {
  UpsertTrustedContactDetails,
  getTrustedContactsByPersonId,
} from "../../../services";
import Spinner from "../../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import TrustedContactForm from "./TrustedContactForm/TrustedContactForm";
import { LookupListItems } from "../../../types/SubscriptionTypes";
import defaultImage from "../../../assets/images/uploadPic.svg";
import deleteIcon from "../../../assets/icons/deleteRedFilled.svg";
import RemoveTrustedContactModal from "./Modals/RemoveTrustedContactModal";

type Props = {
  profile: IServiceUserInfo;
  isVisible: boolean;
  setVisibility: Function;
  updateViewProfileDetails: Function;
};

function ViewProfileDrawer({
  profile,
  isVisible,
  setVisibility,
  updateViewProfileDetails,
}: Props) {
  const [activeProfileTab, setActiveProfileTab] = useState<string>("1");
  const [isOpenEditProfile, setIsOpenEditProfile] = useState<boolean>(false);
  const [trustedContacts, setTrustedContacts] = useState<TrustedContact[]>([]);
  const [initialValues, setInitialValues] = useState<TC_InitialValues>(
    defaultTC_InitialValues
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenAddOrEditTCDrawer, setIsOpenAddOrEditTCDrawer] = useState<
    boolean
  >(false);
  const [title, setTitle] = useState<"Add" | "Edit">("Add");
  const [
    isRemoveTrustedContactModalVisible,
    setIsRemoveTrustedContactModalVisible,
  ] = useState<boolean>(false);

  const {
    relationshipTypesCtx,
    getRelationshipList,
  } = useServiceUserCreationCtx();
  const [relationshipTypes] = relationshipTypesCtx;

  const shouldDisplay = useMemo(() => {
    const isSameAsHomeAddress =
      JSON.stringify(profile.personAttributes.homeAddress) ===
      JSON.stringify(profile.personAttributes.mailingAddress);

    return {
      dateOfBirth: !!getInitialDateOfBirth(profile.dateOfBirth),
      accommodations: profile?.wandaAccommodations?.wheelChairTravel,
      email: !!profile.personAttributes.personContact.wandaEmail,
      mailingAddress: !isSameAsHomeAddress,
    };
  }, [profile]);

  const { TabPane } = Tabs;

  function handleTabChange(activeKey: string) {
    setActiveProfileTab(activeKey);
  }

  function handleOpenEditProfile() {
    setIsOpenEditProfile(true);
  }

  function handleOnCloseMemberProfile() {
    setVisibility(false);
    setActiveProfileTab("1");
  }

  async function getTrustedContacts() {
    try {
      setIsLoading(true);
      const trustedContacts = await getTrustedContactsByPersonId(
        profile.personId
      );

      let relationshipList: LookupListItems[];
      if (!relationshipTypes) relationshipList = await getRelationshipList();
      else relationshipList = relationshipTypes;

      trustedContacts.forEach((tc: TrustedContact) => {
        tc.relationshipName = getRelationshipNameByRelationshipId(
          relationshipList,
          tc.relationship
        );
        tc.personId = profile.personId;
      });

      setTrustedContacts(trustedContacts);
    } catch (error) {
      notification.error({
        message: "Failed to Fetch Trusted Contacts",
      });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    activeProfileTab === "2" &&
      profile.personId !== trustedContacts[0]?.personId &&
      getTrustedContacts();
  }, [profile.personId, activeProfileTab]);

  function handleAddTC() {
    setInitialValues(defaultTC_InitialValues);
    setIsOpenAddOrEditTCDrawer(true);
    setTitle("Add");
  }

  function handleEditTC(personAttributesKeyId: number): void {
    const selectedTC = trustedContacts?.find(
      (tc: TrustedContact) => tc.personAttributesKeyId === personAttributesKeyId
    );

    if (selectedTC) {
      const initialValues = {
        firstName: selectedTC.firstName,
        lastName: selectedTC.lastName,
        phone: selectedTC.phone,
        email: selectedTC.email,
        relationship: selectedTC.relationship,
        personAttributesKeyId: selectedTC.personAttributesKeyId,
      };
      setInitialValues(initialValues);
    }
    setIsOpenAddOrEditTCDrawer(true);
    setTitle("Edit");
  }

  function handleOnCloseAddOrEditTC() {
    setIsOpenAddOrEditTCDrawer(false);
  }

  function closeServiceUserProfileDrawers() {
    setVisibility(false);
    setIsOpenEditProfile(false);
  }

  function updateTrustedContactsOnAddOrUpdate(newTC: TrustedContact) {
    if (newTC.personAttributesKeyId) {
      setTrustedContacts((trustedContacts) =>
        trustedContacts.map((tc: TrustedContact) => {
          if (tc.personAttributesKeyId === newTC.personAttributesKeyId)
            return {
              ...tc,
              ...newTC,
              relationshipName: getRelationshipNameByRelationshipId(
                relationshipTypes,
                newTC.relationship
              ),
            };
          else return tc;
        })
      );
      setIsOpenAddOrEditTCDrawer(false);
    } else {
      getTrustedContacts();
      setIsOpenAddOrEditTCDrawer(false);
    }
  }

  function handleDeleteTrustedContact() {
    setIsRemoveTrustedContactModalVisible(true);
  }

  async function handleRemoveTrustedContact() {
    try {
      const trustedContact = trustedContacts.reduce((acc, tc) => {
        if (tc.personAttributesKeyId === initialValues.personAttributesKeyId) {
          acc.push({ ...tc, isActive: false });
        }
        return acc;
      }, [] as TrustedContact[]);

      const tcData = {
        personId: profile.personId,
        trustedContact,
      };
      await UpsertTrustedContactDetails(tcData);
      setTrustedContacts((trustedContacts) =>
        trustedContacts.filter(
          (tc: TrustedContact) =>
            tc.personAttributesKeyId !== initialValues.personAttributesKeyId
        )
      );
      setIsRemoveTrustedContactModalVisible(false);
      setIsOpenAddOrEditTCDrawer(false);
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to delete Trusted Contact",
      });
    }
  }

  function renderAccommodations() {
    const isFoldable = profile.wandaAccommodations.wheelChairPresent;
    const isTransferable = profile.wandaAccommodations.wcToVehicleTransfer;

    return (
      <>
        <Col className={textStyles.normalText16}>
          Travels with {isFoldable ? "standard foldable" : "non-foldable"}{" "}
          wheelchair.
        </Col>
        <Col className={textStyles.normalText16}>
          {isTransferable ? "Able" : "Not able"} to transfer.
        </Col>
      </>
    );
  }

  function renderAddress(address: Address) {
    return (
      <>
        <Col span={24} className={textStyles.normalText16}>
          {address.address1}
          {address.address2}
        </Col>
        <Col span={24} className={textStyles.normalText16}>
          {address.city},{address.state} {address.zip}
        </Col>
      </>
    );
  }

  function renderBasicInfo(title: string, content: string) {
    return (
      <Row className={styles.marginTop16}>
        <Col span={24} className={textStyles.secondaryNormalText12}>
          {title}
        </Col>
        <Col span={24} className={textStyles.normalText16}>
          {content}
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Drawer
        placement="right"
        className={tabStyles.drawerContainer}
        onClose={handleOnCloseMemberProfile}
        visible={isVisible}
      >
        <Row justify="center" align="middle" className={styles.profileHeader}>
          <img
            src={profile.profileImageUrl || defaultImage}
            alt="profile_picture"
          />
          <Col className={styles.memberName}>
            {profile.firstName} {profile.lastName}
          </Col>
          {shouldDisplay.dateOfBirth && (
            <Col className={textStyles.normalText16}>
              {profile.age} years old
            </Col>
          )}
        </Row>

        <Tabs
          className={tabStyles.tabsContainer}
          onChange={handleTabChange}
          activeKey={activeProfileTab}
        >
          <TabPane tab="Profile" key="1">
            <Row justify="space-between" align="middle">
              <Col className={textStyles.normalText20}>Service user</Col>
              <Col>
                <Button
                  className={buttonStyles.iconBtn}
                  onClick={handleOpenEditProfile}
                >
                  <img src={editIcon} alt="edit_profile" />
                </Button>
              </Col>
            </Row>

            {shouldDisplay.dateOfBirth &&
              renderBasicInfo(
                "Date of birth",
                moment(profile.dateOfBirth).format("MM/DD/YYYY")
              )}

            {shouldDisplay.accommodations && (
              <Row className={styles.marginTop16}>
                <Col span={24} className={textStyles.secondaryNormalText12}>
                  Accommodations
                </Col>

                {renderAccommodations()}
              </Row>
            )}

            {renderBasicInfo(
              "Phone",
              formatPhoneNumber(
                profile.personAttributes.personContact.primaryPhone
              )
            )}

            {shouldDisplay.email &&
              renderBasicInfo(
                "Email address",
                profile.personAttributes.personContact.wandaEmail
              )}

            <Row className={styles.marginTop16}>
              <Col span={24} className={textStyles.secondaryNormalText12}>
                Home address
              </Col>

              {renderAddress(profile.personAttributes.homeAddress)}
            </Row>

            <Row className={styles.marginTop16}>
              <Col span={24} className={textStyles.secondaryNormalText12}>
                Mailing address
              </Col>

              {shouldDisplay.mailingAddress ? (
                renderAddress(profile.personAttributes.mailingAddress)
              ) : (
                <Col
                  className={`${textStyles.disabledNormalText14} ${styles.marginTop16}`}
                >
                  <Checkbox
                    defaultChecked
                    disabled
                    className={styles.disabledCheckbox}
                  />
                  Same as home address
                </Col>
              )}
            </Row>
          </TabPane>

          <TabPane tab="Trusted Contacts" key="2">
            {isLoading ? (
              <Spinner />
            ) : (
              <Row className={styles.viewTC_Container}>
                <Col span={24}>
                  <Row justify="space-between" className={styles.marginDown8}>
                    <Col className={textStyles.normalText20}>
                      Trusted contacts
                    </Col>
                    {trustedContacts.length < 5 && (
                      <Col>
                        <Button
                          className={buttonStyles.iconBtn}
                          onClick={handleAddTC}
                        >
                          <img src={plusIcon} alt="add_trusted_contact" />
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Col>

                <Col
                  span={24}
                  className={`${textStyles.normalText14} ${styles.marginDown16}`}
                >
                  Trusted contacts will receive communication about{" "}
                  <strong>{profile.firstName}'s </strong>
                  transportation & other services.
                </Col>

                {trustedContacts?.map((tc: TrustedContact) => (
                  <Col
                    key={tc.personAttributesKeyId}
                    span={24}
                    className={styles.marginTop16}
                  >
                    <Card className={styles.tcCardContainer}>
                      <Row
                        justify="space-between"
                        className={styles.marginDown8}
                      >
                        <Col className={textStyles.normalText16}>
                          {tc.firstName} {tc.lastName}
                        </Col>
                        <Col className={textStyles.tertiaryNormalText14}>
                          {tc.relationshipName}
                        </Col>
                      </Row>

                      <Row className={textStyles.secondaryNormalText14}>
                        <Col span={24}>{formatPhoneNumber(tc.phone)}</Col>
                        <Col span={24}>{tc.email}</Col>
                      </Row>

                      <Row justify="end">
                        <Col>
                          <Button
                            className={buttonStyles.iconBtn}
                            onClick={() =>
                              handleEditTC(tc.personAttributesKeyId)
                            }
                          >
                            <img src={editIcon} alt="edit_trusted_contact" />
                          </Button>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </TabPane>
        </Tabs>
      </Drawer>

      {isOpenEditProfile && (
        <EditProfileDrawer
          profile={profile}
          updateViewProfileDetails={updateViewProfileDetails}
          isVisible={isOpenEditProfile}
          setVisibility={setIsOpenEditProfile}
          closeServiceUserProfileDrawers={closeServiceUserProfileDrawers}
        />
      )}

      {isOpenAddOrEditTCDrawer && (
        <Drawer
          title={
            <Row className={textStyles.normalText20}>
              {title} Trusted Contact
            </Row>
          }
          placement="right"
          className={drawerStyles.drawerContainer}
          onClose={handleOnCloseAddOrEditTC}
          visible={isOpenAddOrEditTCDrawer}
        >
          <TrustedContactForm
            profile={profile}
            initialValues={initialValues}
            trustedContacts={trustedContacts}
            updateTrustedContactsOnAddOrUpdate={
              updateTrustedContactsOnAddOrUpdate
            }
            handleOnClose={handleOnCloseAddOrEditTC}
          />

          <Row justify="center" className={styles.marginDown96}>
            {title === "Edit" && (
              <Button
                onClick={handleDeleteTrustedContact}
                className={buttonStyles.deleteRedBtn16}
              >
                <img src={deleteIcon} alt="delete_service_user" />
                Delete contact
              </Button>
            )}
          </Row>
        </Drawer>
      )}

      <RemoveTrustedContactModal
        serviceUserName={`${initialValues.firstName} ${initialValues.lastName}`}
        visible={isRemoveTrustedContactModalVisible}
        setVisibility={setIsRemoveTrustedContactModalVisible}
        handleYesRemove={handleRemoveTrustedContact}
      />
    </>
  );
}

export default ViewProfileDrawer;
